/** Shared builder state and projectile/metadata configuration. */
import { GameConstants } from '../../../../shared/GameConstants';
import { ActionContext } from './types';
import { AbilityMetadata, HomingMetadata } from './AbilityRegistry';
import type { ProjectileConfig, VFXConfig, BuffConfig } from './AbilityBuilderTypes';

import { VFXPrimitive } from '../../../effects/VFXEffectRegistry';
import { AOEConfig } from './behaviors/AOEBehavior';
import { DashConfig } from './behaviors/DashBehavior';
import { ChannelConfig } from './behaviors/ChannelBehavior';
import { HealConfig } from './behaviors/HealBehavior';
import { ShieldConfig } from './behaviors/ShieldBehavior';
import { DebuffConfig } from './behaviors/DebuffBehavior';
import { MeleeConfig } from './behaviors/MeleeBehavior';
import { HitscanConfig } from './behaviors/HitscanBehavior';
import { SummonConfig } from './behaviors/SummonBehavior';
import type { AuraEffectConfig } from './behaviors/AuraEffectBehavior';
import { NovaProjectileConfig } from './behaviors/NovaProjectileBehavior';

import type { TimelineEntry } from './timeline/AbilityTimeline';
import type { ProjectileModifiers, HitEffectEntry, ConditionFn, AbilityModifier } from './modifiers/types';

import { registerAuraVFX } from './AbilityPresets';

import { loadTomlAbilityData } from './TomlAbilityLoader';
import { type CasterAuraConfig } from './AbilityBuilderFactory';

import { resolveAbilityArchetype, ENABLE_ARCHETYPE_CAST_FALLBACK, archCastId } from '../../../../config/abilities/vfxArchetypes.config';

export class AbilityBuilderCore {
  protected metadata: Partial<AbilityMetadata>;
  protected windUpDuration = 0;
  protected windUpVFX: string | null = null;
  protected projectileConfig: ProjectileConfig | null = null;
  protected projectileVFX: VFXConfig | null = null;
  protected instantEffect: ((ctx: ActionContext) => void) | null = null;
  // Buffs compose: several abilities intentionally call withBuff() more than
  // once for multi-stat payloads. A single slot silently discarded every
  // earlier buff (and its explicit VFX) when a later call was chained.
  protected buffConfigs: BuffConfig[] = [];
  protected multiProjectiles: { count: number; stagger: number; config: ProjectileConfig } | null = null;
  protected aoeConfig: AOEConfig | null = null;
  protected dashConfig: DashConfig | null = null;
  protected channelConfig: ChannelConfig | null = null;
  protected healConfig: HealConfig | null = null;
  protected shieldConfig: ShieldConfig | null = null;
  protected debuffConfig: DebuffConfig | null = null;
  protected onCompleteCallback: (() => void) | null = null;
  protected _composedVFXLayers: VFXPrimitive[] | null = null;
  protected _timelineDuration: number | null = null;
  public _timelineEntries: TimelineEntry[] = [];
  protected projectileModifiers: ProjectileModifiers = {};
  protected hitEffects: HitEffectEntry[] = [];
  protected conditionals: Array<{ condition: ConditionFn; modifier: AbilityModifier }> = [];
  protected meleeConfig: MeleeConfig | null = null;
  protected hitscanConfig: HitscanConfig | null = null;
  protected summonConfig: SummonConfig | null = null;
  protected _chainInto: { abilityId: string; windowMs: number } | null = null;
  protected novaConfig: NovaProjectileConfig | null = null;
  protected passiveConfig: { trigger: string; chance?: number; cooldownMs?: number; effect: (ctx: ActionContext) => void } | null = null;
  protected toggleConfig: { resourceDrainPerSecond?: number; onActivate?: (ctx: ActionContext) => void; onDeactivate?: (ctx: ActionContext) => void; maxDurationMs?: number } | null = null;
  protected chargesConfig: { maxCharges: number; regenTimeMs: number; consumePerCast: number } | null = null;
  protected coneAOEConfig: { damage: number | ((ctx: ActionContext) => number); angle: number; distance: number; ticks?: number; vfxId?: string } | null = null;
  protected lineAOEConfig: { damage: number | ((ctx: ActionContext) => number); length: number; width: number; ticks?: number; vfxId?: string } | null = null;
  protected _auraEffectConfig: AuraEffectConfig | null = null;
  protected _casterAuraConfig: CasterAuraConfig | null = null;

  constructor(id: string) {
    this.metadata = { id, name: id, description: '' };
  }

  /** Override automatic aiming without changing the version-1 network payload. */
  withAimMode(mode: 'assisted' | 'straight' | 'ground' | 'none' | import('../../../../shared/aiming/aimMetadata').AimCategory): this {
    const aliases = {
      assisted: 'projectile_assisted', straight: 'projectile_straight',
      ground: 'ground_target', none: 'legacy_direct',
    } as const;
    this.metadata.aimCategory = mode in aliases ? aliases[mode as keyof typeof aliases]
      : mode as import('../../../../shared/aiming/aimMetadata').AimCategory;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Metadata Configuration
  // ---------------------------------------------------------------------------

  withName(name: string): this {
    this.metadata.name = name;
    return this;
  }

  withDescription(description: string): this {
    this.metadata.description = description;
    return this;
  }

  withManaCost(cost: number): this {
    this.metadata.manaCost = cost;
    return this;
  }

  withCooldown(ms: number): this {
    this.metadata.cooldown = ms;
    return this;
  }

  /**
   * Load all numeric balance data from TOML-generated AbilityDataDef.
   * Call this instead of importing from Balance.ts files.
   * Behavior (projectile shape, homing, AOE pattern) stays in code.
   */
  withTomlData(abilityId: string): this {
    const patch = loadTomlAbilityData(abilityId, this.metadata.id ?? abilityId);
    if (!patch) return this;
    Object.assign(this.metadata, patch.metadata);
    if (patch.windUpDuration !== undefined) {
      this.windUpDuration = patch.windUpDuration;
    }
    this.applyTomlVfxDefaults();
    return this;
  }

  /**
   * Derive status-VFX metadata from the TOML `vfx` table so behaviors can
   * render without per-ability code:
   *  - `cast_effect`  → `metadata.vfx.cast` (resolved against the registry at
   *    execute time — crafted effects like `frost_shield` win when they exist)
   *  - `aura_color`   → registers a generic pulsing `${id}_aura` effect and
   *    sets `metadata.vfx.aura` + `metadata.vfx.color`
   *
   * Never overwrites values already set; later explicit builder calls
   * (`.withCastEffect()`, `.withAura()`, `.withVFX()`) also override these
   * since `withTomlData` is conventionally first in the chain. This is what
   * makes Shield/Buff status VFX work for ALL TOML abilities (~500), not just
   * hand-wired ones.
   */
  protected applyTomlVfxDefaults(): void {
    const tomlVfx = (this.metadata as { tomlVfx?: { castEffect?: string; auraColor?: string; impactEffect?: string } }).tomlVfx;
    if (!tomlVfx) return;
    if (!this.metadata.vfx) this.metadata.vfx = {};
    const vfx = this.metadata.vfx;

    if (tomlVfx.castEffect && !vfx.cast) {
      vfx.cast = tomlVfx.castEffect;
    }

    if (tomlVfx.impactEffect && !vfx.impact) {
      vfx.impact = tomlVfx.impactEffect;
    }

    if (tomlVfx.auraColor) {
      const color = parseInt(tomlVfx.auraColor.replace('#', ''), 16);
      if (!Number.isNaN(color)) {
        if (vfx.color === undefined) vfx.color = color;
        if (!vfx.aura) {
          // Determine followTarget from ability's target field:
          // 'enemy'/'point' abilities debuff the target; self/utility buffs follow the caster.
          const abilityTarget = (this.metadata as { target?: string }).target;
          const followTarget = abilityTarget === 'enemy' || abilityTarget === 'point' ? 'target' : 'player';
          vfx.aura = registerAuraVFX(this.metadata.id!, color, undefined, true, followTarget);
        }
      }
    }

    // F2b: archetype cast effect fallback when no authored cast effect is
    // present. Guarded by ENABLE_ARCHETYPE_CAST_FALLBACK so the new
    // archetype-derived cast VFX can be toggled in a single place.
    if (!vfx.cast && ENABLE_ARCHETYPE_CAST_FALLBACK) {
      const cls = (this.metadata as { class?: string }).class;
      const school = (this.metadata as { school?: string }).school;
      if (cls && school) {
        const archetype = resolveAbilityArchetype(cls, school);
        if (archetype) {
          vfx.cast = archCastId(cls, school);
        }
      }
    }
  }

  withIcon(icon: string): this {
    this.metadata.icon = icon;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Cast Timeline Configuration
  // ---------------------------------------------------------------------------

  withWindUp(durationMs: number, vfxId?: string): this {
    this.windUpDuration = durationMs;
    this.metadata.castTime = durationMs;
    if (vfxId) this.windUpVFX = vfxId;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Projectile Configuration
  // ---------------------------------------------------------------------------

  withProjectile(config: ProjectileConfig): this {
    this.projectileConfig = config;

    // Map to metadata for Browser UI
    const homing: HomingMetadata | undefined = typeof config.homing === 'object'
      ? {
        strength: config.homing.strength ?? GameConstants.Combat.HOMING_STRENGTH_BASIC,
        maxTurnRate: config.homing.maxTurnRate ?? GameConstants.Combat.HOMING_MAX_TURN_RATE,
        delay: config.homing.delay ?? GameConstants.Combat.HOMING_DELAY_BASIC
      }
      : (config.homing === true ? {
        strength: GameConstants.Combat.HOMING_STRENGTH_BASIC,
        maxTurnRate: GameConstants.Combat.HOMING_MAX_TURN_RATE,
        delay: GameConstants.Combat.HOMING_DELAY_BASIC
      } : undefined);

    this.metadata.projectile = {
      damage: typeof config.damage === 'number' ? config.damage : 'Dynamic',
      speed: config.speed,
      shape: config.shape,
      color: config.color,
      // Visual fields — carried so every render path (local single,
      // local multi, AND the remote broadcast path) resolves the same
      // size/feel. Previously dropped here, which forced each path to
      // fall back to its own hardcoded default → local/remote size drift.
      width: config.width,
      height: config.height,
      rotationSpeed: config.rotationSpeed,
      gravity: config.gravity,
      lifespan: config.lifespan,
      strokeColor: config.strokeColor,
      strokeWidth: config.strokeWidth,
      homing
    };

    // Store the original damage function for behaviors to evaluate at cast time
    if (typeof config.damage === 'function') {
      this.metadata._damageFn = config.damage;
    }

    return this;
  }

  withMultiProjectile(count: number, staggerMs: number, config: ProjectileConfig): this {
    this.multiProjectiles = { count, stagger: staggerMs, config };

    const homing: HomingMetadata | undefined = typeof config.homing === 'object'
      ? {
        strength: config.homing.strength ?? GameConstants.Combat.HOMING_STRENGTH_BASIC,
        maxTurnRate: config.homing.maxTurnRate ?? GameConstants.Combat.HOMING_MAX_TURN_RATE,
        delay: config.homing.delay ?? GameConstants.Combat.HOMING_DELAY_BASIC,
      }
      : (config.homing === true ? {
        strength: GameConstants.Combat.HOMING_STRENGTH_BASIC,
        maxTurnRate: GameConstants.Combat.HOMING_MAX_TURN_RATE,
        delay: GameConstants.Combat.HOMING_DELAY_BASIC,
      } : undefined);

    // Map to metadata (summarized)
    this.metadata.projectile = {
      damage: typeof config.damage === 'number' ? `${config.damage} x ${count}` : 'Dynamic',
      speed: config.speed,
      shape: config.shape,
      color: config.color,
      // Visual fields — same rationale as withProjectile above.
      width: config.width,
      height: config.height,
      rotationSpeed: config.rotationSpeed,
      gravity: config.gravity,
      lifespan: config.lifespan,
      strokeColor: config.strokeColor,
      strokeWidth: config.strokeWidth,
      homing,
      // `randomizeY` was historically authored by Barrage but silently
      // dropped. Multi casts use the same value as a reproducible fan span;
      // explicit spreadY is the clearer new spelling.
      spreadY: config.spreadY ?? config.randomizeY,
    };

    // Store the original damage function for behaviors to evaluate at cast time
    const damage = config.damage;
    this.metadata._damageFn = typeof damage === 'function' ? damage : () => damage;

    return this;
  }

  // ---------------------------------------------------------------------------
  // VFX Configuration
  // ---------------------------------------------------------------------------

}
